import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { apiFetch } from "@/lib/api.js";
import { useAuth } from "@/auth/useAuth.js";

const AttentionItem = z.object({
  kind: z.enum(["lead", "deal", "alert"]),
  source: z.enum(["inquiry", "booking", "seller", "valuation"]).nullable(),
  id: z.string().uuid(),
  title: z.string(),
  detail: z.string(),
  since: z.string(),
  waiting_days: z.number(),
});
export type AttentionItem = z.infer<typeof AttentionItem>;

const AttentionResponse = z.object({
  items: z.array(AttentionItem),
  total: z.number(),
  days: z.number(),
});

const DismissResponse = z.object({ ok: z.boolean(), snooze_until: z.string() });

export function useAttention(days = 3) {
  const { session } = useAuth();
  const token = session?.access_token;
  return useQuery({
    queryKey: ["attention", days],
    enabled: !!token,
    queryFn: () => apiFetch("/api/v1/admin/attention", token!, AttentionResponse, { query: { days } }),
  });
}

export function useDismissAttention() {
  const { session } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { kind: AttentionItem["kind"]; id: string; source?: AttentionItem["source"] }) =>
      apiFetch("/api/v1/admin/attention/dismiss", session!.access_token, DismissResponse, {
        method: "POST", body: input,
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["attention"] }),
  });
}
